import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import "./styles/aerosToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { ThemeProvider } from "@material-ui/core/styles";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import { AEROS } from "./styles/aerosTokens";
import { createAerosTheme } from "./styles/aerosTheme";
import { i18n } from "./translate/i18n";

import "./styles/animations.css";
import "./styles/viewport.css";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  // As telas chamam i18n.t() direto (sem hooks do react-i18next); para a
  // troca de idioma valer sem reload, o tema muda de identidade a cada
  // languageChanged e a árvore inteira re-renderiza.
  const [lang, setLang] = useState(i18n.language);
  useEffect(() => {
    const onLang = (l) => setLang(l);
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, []);
  const appColorLocalStorage =
    localStorage.getItem("primaryColorLight") ||
    localStorage.getItem("primaryColorDark") ||
    AEROS.defaultPrimary;
  const appNameLocalStorage = localStorage.getItem("appName") || "";
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  // "system" segue prefers-color-scheme; light/dark são fixos.
  const systemMode = () => (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark");
  const [preference, setPreference] = useState(preferredTheme || "dark");
  const [mode, setMode] = useState(preferredTheme === "system" ? systemMode() : preferredTheme || "dark");
  useEffect(() => {
    if (preference !== "system" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setMode(systemMode());
    onChange();
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, [preference]);
  const [primaryColorLight, setPrimaryColorLight] =
    useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] =
    useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setPreference("fixed");
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setColorMode: (pref) => {
        window.localStorage.setItem("preferredTheme", pref);
        setPreference(pref);
        if (pref !== "system") setMode(pref);
      },
      preference,
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode, preference]
  );

  const theme = useMemo(
    () =>
      createAerosTheme(
        {
          mode,
          primaryColor: mode === "light" ? primaryColorLight : primaryColorDark,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight)
              return appLogoDark;
            return appLogoLight;
          },
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight)
              return appLogoLight;
            return appLogoDark;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight, lang] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (preference !== "system") window.localStorage.setItem("preferredTheme", mode);
  }, [mode, preference]);

  // O splash em public/index.html roda antes do React e lê estas chaves para
  // mostrar a logomarca em vez do monograma (ver o script no fim do index.html).
  // O splash é sempre escuro, então recebe a logo "calculada" do tema escuro
  // (a do claro quando só ela foi configurada), como o painel de /login.
  useEffect(() => {
    try {
      const splashLogo =
        appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight
          ? appLogoLight
          : appLogoDark;
      window.localStorage.setItem("splashLogo", splashLogo);
    } catch (e) {}
  }, [appLogoLight, appLogoDark]);

  useEffect(() => {
    getPublicSetting("primaryColorLight")
      .then((color) => {
        setPrimaryColorLight(color || AEROS.defaultPrimary);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("primaryColorDark")
      .then((color) => {
        setPrimaryColorDark(color || AEROS.defaultPrimary);
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoLight")
      .then((file) => {
        setAppLogoLight(
          file ? getBackendUrl() + "/public/" + file : defaultLogoLight
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoDark")
      .then((file) => {
        setAppLogoDark(
          file ? getBackendUrl() + "/public/" + file : defaultLogoDark
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appLogoFavicon")
      .then((file) => {
        setAppLogoFavicon(
          file ? getBackendUrl() + "/public/" + file : defaultLogoFavicon
        );
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    getPublicSetting("appName")
      .then((name) => {
        setAppName(name || "Pulse");
      })
      .catch((error) => {
        console.log("Error reading setting", error);
        setAppName("Pulse");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      "--primaryColor",
      mode === "light" ? primaryColorLight : primaryColorDark
    );
    // Exposto para CSS que não pode ler o tema do MUI (ex.: toasts do
    // react-toastify, que renderizam fora da árvore de tema via portal).
    root.dataset.theme = mode;
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon
        url={
          appLogoFavicon
            ? appLogoFavicon
            : defaultLogoFavicon
        }
      />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
              <Routes />
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;
